// src/components/HistoryPage.jsx
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate }  from 'react-router-dom';
import PageWrapper from './PageWrapper';
import axiosInstance from '../axiosInstance';
import { HistoryIcon } from '../common/Icons';
import { LanguageContext } from '../contexts/LanguageContext'; // Import LanguageContext
import { ToastContainer, toast } from 'react-toastify'; // Import ToastContainer and toast

// Translations object, moved outside the component to prevent re-creation on every render
const translations = {
  en: {
    betHistory: "Bet History",
    loadingHistory: "Loading bet history...",
    noHistory: "No bet history available.",
    sessionExpired: "Your session has expired or is invalid. Redirecting to login...",
    networkError: "Network or server error fetching game history.",
    error: "Error",
    goToLogin: "Go to Login",
    no: "No",
    stake: "Stake",
    gameWinning: "Game Winning",
    winnerCards: "Winner Cards",
    yourCards: "Your Cards",
    date: "Date",
    result: "Result",
    birr: "Birr",
    won: "Won",
    lost: "Lost"
  },
  am: {
    betHistory: "የውርርድ ታሪክ",
    loadingHistory: "የውርርድ ታሪክ በመጫን ላይ...",
    noHistory: "ምንም የውርርድ ታሪክ የለም።",
    sessionExpired: "የእርስዎ ክፍለ ጊዜ አልቋል ወይም ልክ ያልሆነ ነው። ወደ መግቢያ በመዞር ላይ...",
    networkError: "የአውታረ መረብ ወይም የአገልጋይ ስህተት የጨዋታ ታሪክ በሚያመጣበት ጊዜ።",
    error: "ስህተት",
    goToLogin: "ወደ መግቢያ ይሂዱ",
    no: "ቁጥር",
    stake: "ውርርድ",
    gameWinning: "የጨዋታ አሸናፊ",
    winnerCards: "አሸናፊ ካርዶች",
    yourCards: "የእርስዎ ካርዶች",
    date: "ቀን",
    result: "ውጤት",
    birr: "ብር",
    won: "አሸነፈ",
    lost: "ተሸነፈ"
  },
  or: {
    betHistory: "Seenaa Betii",
    loadingHistory: "Seenaa betii fe'amaa jira...",
    noHistory: "Ammas seenaan betii hin jiru.",
    sessionExpired: "Sesshiniin keessan xumureera ykn hin hojjetu. Gara seensaatti deebi'aa...",
    networkError: "Dogoggora networkii ykn serverii seenaa taphaa argachuuf.",
    error: "Dogoggora",
    goToLogin: "Gara seensaatti deebi'i",
    no: "Lakkoofsa",
    stake: "Kaffaltii",
    gameWinning: "Moo'ataa Taphaa",
    winnerCards: "Kaardota Moo'ataa",
    yourCards: "Kaardota keessan",
    date: "Guyyaa",
    result: "Argamummaa",
    birr: "Birr",
    won: "Moo'ate",
    lost: "Mo'ame"
  },
  ti: {
    betHistory: "ታሪኽ ውርርድ",
    loadingHistory: "ታሪኽ ውርርድ ይጽዕን ኣሎ...",
    noHistory: "እስካሁን ምንም የውርርድ ታሪክ የለም።",
    sessionExpired: "ናይ'ዚ ስራሕ ግዜኹም ወዲኡ ወይ ድማ ቅኑዕ ኣይኮነን። ናብ ምእታው ይምለስ ኣሎ...",
    networkError: "ጌጋ መርበብ ወይ ሰርቨር ናይ ጸወታ ታሪኽ ምምጻእ።",
    error: "ጌጋ",
    goToLogin: "ናብ ምእታው ይኸይድ",
    no: "ቁጽሪ",
    stake: "ውርርድ",
    gameWinning: "ዓወት ጸወታ",
    winnerCards: "ካርዳት ዓወት",
    yourCards: "ካርዳትኩም",
    date: "ዕለት",
    result: "ውጽኢት",
    birr: "ብር",
    won: "ተዓዊቱ",
    lost: "ተሳዒሩ"
  },
  wo: {
    betHistory: "Seenaa Betii",
    loadingHistory: "Seenaa betii fe'amaa jira...",
    noHistory: "Ammas seenaan betii hin jiru.",
    sessionExpired: "Sesshiniin keessan xumureera ykn hin hojjetu. Gara seensaatti deebi'aa...",
    networkError: "Dogoggora networkii ykn serverii seenaa taphaa argachuuf.",
    error: "Dogoggora",
    goToLogin: "Gara seensaatti deebi'i",
    no: "Lakkoofsa",
    stake: "Kaffaltii",
    gameWinning: "Moo'ataa Taphaa",
    winnerCards: "Kaardota Moo'ataa",
    yourCards: "Kaardota keessan",
    date: "Guyyaa",
    result: "Argamummaa",
    birr: "Birr",
    won: "Moo'ate",
    lost: "Mo'ame"
  }
};

/**
 * HistoryPage component displays the user's game history, including
 * stake amount, winning amount, cards played, date, and result.
 * It fetches data from the API and provides translation support.
 */
const HistoryPage = ({ setCurrentPage, ...props }) => { // Destructure setCurrentPage
  const { language } = useContext(LanguageContext); // Use LanguageContext to get current language
  const navigate = useNavigate(); // Hook for navigation
  const [gameHistory, setGameHistory] = useState([]); // State to store fetched game history
  const [loading, setLoading] = useState(true); // Loading state for data fetching
  const [error, setError] = useState(''); // State for critical full-page errors (e.g., session expired)

  // Memoized translation helper function to prevent unnecessary re-renders
  const t = useCallback((key, variables = {}) => {
    let translation = translations[language]?.[key] || translations.en[key] || key;
    for (const [placeholder, value] of Object.entries(variables)) {
      translation = translation.replace(`{${placeholder}}`, value);
    }
    return translation;
  }, [language]); // Removed 'translations' from dependency array as it's now stable

  // Toast utility function for displaying notifications
  const showToast = useCallback((message, type = 'error') => {
    toast[type](message, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  }, []);

  // Callback to fetch game history from the backend API
  const fetchGameHistory = useCallback(async () => {
    try {
      setLoading(true); // Set loading state to true before API call
      const response = await axiosInstance.get('/user/game-history'); // API call
      if (response.data.success) {
        // Process fetched data: format dates and ensure numeric types
        const formattedHistory = response.data.gameHistory.map((game, index) => {
          let formattedDateDisplay = '--';
          // Check if date exists and is in the expected string format (e.g., "DD/MM/YYYY, HH:MM")
          if (game.date && typeof game.date === 'string' && game.date.includes('/')) {
            const [datePart, timePart] = game.date.split(', ');
            const [day, month, year] = datePart.split('/');
            const [hour, minute] = timePart.split(':');

            // Construct a Date object (month is 0-indexed in JS Date, so month - 1)
            const parsedDate = new Date(year, month - 1, day, hour, minute, 0);

            // Validate if the parsed date is a valid date
            if (!isNaN(parsedDate.getTime())) {
              formattedDateDisplay = parsedDate.toLocaleString('en-US', {
                month: 'numeric',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                second: '2-digit',
                hour12: true
              });
            }
          }

          return {
            ...game, // Keep all original properties of the game object
            no: index + 1, // Assign a sequential number for display
            stake_amount: parseFloat(game.stake_amount) || 0, // Convert stake to float, default to 0
            win_amount: parseFloat(game.win_amount) || 0, // Convert win amount to float, default to 0
            date: formattedDateDisplay, // Use the newly formatted date string
          };
        });
        setGameHistory(formattedHistory); // Update state with processed history
        setError(''); // Clear any previous errors on successful fetch
      } else {
        // Show toast for non-critical API messages, set generic error for page display
        showToast(response.data.message || t('failedToFetchGameHistory'), 'error');
        setError(t('error')); // Still set a generic error for the page
      }
    } catch (err) {
      console.error('Error fetching game history:', err); // Log the error for production monitoring
      // Handle authentication/authorization errors specifically
      if (err.response?.status === 401 || err.response?.status === 403) {
        showToast(t('sessionExpired'), 'error');
        setError(t('sessionExpired'));
        localStorage.removeItem('token'); // Clear token on session expiration
        navigate('/login'); // Redirect to login page
      } else {
        // Handle other network or server errors
        showToast(err.response?.data?.message || t('networkError'), 'error');
        setError(t('networkError'));
      }
    } finally {
      setLoading(false); // Always set loading to false after fetch attempt
    }
  }, [navigate, t, showToast]); // Dependencies for useCallback

  // Effect hook to fetch game history on component mount and set the current page title
  useEffect(() => {
    setCurrentPage(t('betHistory')); // Set current page title using translation
    fetchGameHistory(); // Call the fetch function
  }, [setCurrentPage, fetchGameHistory, t]); // Removed 'props' from dependencies

  // Render loading state while data is being fetched
  if (loading) {
    return (
      <PageWrapper {...props}>
        <div className="flex justify-center items-center h-screen">
          <p className="text-gray-700 text-lg">{t('loadingHistory')}</p>
        </div>
      </PageWrapper>
    );
  }

  // Render a critical error page if session expired or network error occurred
  if (error && (error === t('sessionExpired') || error === t('networkError'))) {
    return (
      <PageWrapper {...props}>
        <div className="flex flex-col justify-center items-center h-screen text-red-600">
          <p className="text-lg mb-2">{t('error')}: {error}</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-md"
          >
            {t('goToLogin')}
          </button>
        </div>
      </PageWrapper>
    );
  }

  // Main history display content
  return (
    <PageWrapper {...props} backgroundClass="bg-gradient-to-br from-purple-800 to-blue-900 text-white min-h-screen">
      <ToastContainer /> {/* ToastContainer for displaying notifications */}
      <div className="relative max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Page title with an icon */}
        <h1 className="text-3xl font-bold text-center mb-8 flex items-center justify-center space-x-3">
          <HistoryIcon className="w-8 h-8 text-yellow-400" />
          <span>{t('betHistory')}</span>
        </h1>

        {/* Table container for game history */}
        <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl shadow-xl p-6 border border-white border-opacity-20 overflow-hidden">
          {gameHistory.length === 0 ? (
            // Message displayed when no history is available
            <p className="text-gray-300 text-center py-8">{t('noHistory')}</p>
          ) : (
            // Responsive table for game history
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-700">
                {/* Table Header */}
                <thead className="bg-gray-700 bg-opacity-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">{t('no')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">{t('stake')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">{t('gameWinning')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">{t('winnerCards')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">{t('yourCards')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">{t('date')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">{t('result')}</th>
                  </tr>
                </thead>
                {/* Table Body */}
                <tbody className="divide-y divide-gray-800">
                  {gameHistory.map((game, index) => (
                    <tr key={game.id} className="hover:bg-gray-700 hover:bg-opacity-20">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-200">{game.no}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-200">
                        {game.stake_amount.toFixed(2)} {t('birr')}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-200">
                        {/* Display win amount or '--' if not available */}
                        {game.win_amount ? game.win_amount.toFixed(2) + ` ${t('birr')}` : '--'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-200">
                        {/* Display winner cards or '--' if not available */}
                        {game.winner_cards || '--'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-200">
                        {/* Display user's cards or '--' if not available */}
                        {game.your_cards || '--'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-200">
                        {/* Display formatted date */}
                        {game.date}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {/* Display result (Won/Lost) with corresponding colors */}
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          game.is_winner ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {game.is_winner ? t('won') : t('lost')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </PageWrapper>
  );
};

export default HistoryPage;
